import { Chip } from "components/DesignSystem/Chips/Chips";
import { Filter } from "components/DesignSystem/Filter/Filter";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { UpdateFilterFunction } from "hooks/useFilter";
import {
  NEXUS_TYPE_ECONOMIC,
  NEXUS_TYPE_PHYSICAL,
} from "constants/nexusTrackerStatus";
import { statusMapping } from "./SalesNexusTracker";

export type NexusTrackerFilterValues = {
  NEXUS_TYPE: string[];
  STATUS_FILTER: (keyof typeof statusMapping)[];
};

type NexusTrackerFilterProps = {
  values: NexusTrackerFilterValues;
  updateFilter: UpdateFilterFunction<NexusTrackerFilterValues>;
  onToggleStatus: (status: keyof typeof statusMapping) => void;
};

const FILTER_PILLS: (keyof typeof statusMapping)[] = [
  "EXPOSED",
  "APPROACHING",
  "MONITORING",
];

const NEXUS_TYPE_OPTIONS = [
  { label: "Economic", value: NEXUS_TYPE_ECONOMIC },
  { label: "Physical", value: NEXUS_TYPE_PHYSICAL },
];

const STATUS_FILTER_OPTIONS: (keyof typeof statusMapping)[] = [
  "EXPOSED",
  "APPROACHING",
  "REMITTING",
  "EXEMPT",
  "MONITORING",
];

const NexusTypeFilter = ({
  values,
  updateFilter,
}: {
  values: NexusTrackerFilterValues;
  updateFilter: UpdateFilterFunction<NexusTrackerFilterValues>;
}) => {
  const toggleNexusType = (value: string, checked: boolean) => {
    const nextValues = checked
      ? [...values.NEXUS_TYPE, value]
      : values.NEXUS_TYPE.filter((type) => type !== value);
    updateFilter("NEXUS_TYPE", nextValues);
  };

  return (
    <div className="t-flex t-flex-col t-gap-3">
      {NEXUS_TYPE_OPTIONS.map((option) => (
        <Checkbox
          key={option.value}
          name={option.value}
          label={option.label}
          checked={values.NEXUS_TYPE.includes(option.value)}
          onChange={(e) => toggleNexusType(option.value, e.target.checked)}
        />
      ))}
    </div>
  );
};

const StatusFilter = ({
  values,
  onToggleStatus,
}: {
  values: NexusTrackerFilterValues;
  onToggleStatus: (status: keyof typeof statusMapping) => void;
}) => {
  return (
    <div className="t-flex t-flex-col t-gap-3">
      {STATUS_FILTER_OPTIONS.map((status) => (
        <Checkbox
          key={status}
          name={status}
          label={statusMapping[status]}
          checked={values.STATUS_FILTER.includes(status)}
          onChange={() => onToggleStatus(status)}
        />
      ))}
    </div>
  );
};

export const NexusTrackerFilter = ({
  values,
  updateFilter,
  onToggleStatus,
}: NexusTrackerFilterProps) => {
  return (
    <Filter.Root
      defaultValue="nexusType"
      title={null}
      capsule={
        <>
          {FILTER_PILLS.map((status) => (
            <Chip
              key={status}
              filterType="status"
              isFixedFilter
              isRemovable
              isActive={values.STATUS_FILTER.includes(status)}
              onClick={() => onToggleStatus(status)}
              onRemove={() => onToggleStatus(status)}
            >
              {statusMapping[status]}
            </Chip>
          ))}

          {values.NEXUS_TYPE.length > 0 && (
            <Chip
              filterType="nexusType"
              isActive
              isRemovable
              onRemove={() => updateFilter("NEXUS_TYPE", [])}
            >
              Nexus type ({values.NEXUS_TYPE.length})
            </Chip>
          )}

          {values.STATUS_FILTER.filter(
            (status) => !FILTER_PILLS.includes(status)
          ).map((status) => (
            <Chip
              key={status}
              filterType="status"
              isActive
              isRemovable
              onRemove={() => onToggleStatus(status)}
            >
              {statusMapping[status]}
            </Chip>
          ))}
        </>
      }
    >
      <Filter.Portal size="small">
        <Filter.List size="small">
          <Filter.ListItem value="nexusType">Nexus type</Filter.ListItem>
          <Filter.ListItem value="status">Status</Filter.ListItem>
        </Filter.List>
        <Filter.Body value="nexusType" block>
          <NexusTypeFilter values={values} updateFilter={updateFilter} />
        </Filter.Body>
        <Filter.Body value="status" block>
          <StatusFilter values={values} onToggleStatus={onToggleStatus} />
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};
